import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    Button,
    Paper,
    Grid,
    Card,
    IconButton,
    Chip,
    Pagination,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Visibility from '@mui/icons-material/Visibility';
import Search from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import { getAllPostSeasonActivitiesAction, getAllDistrictsAction, getAllMandalsAction, getAllSeasonsAction , getAllZonesAction, getAllRegionsAction, getAllAreasAction} from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import PostActivityDetailsView from './PostActivityDetailsView';

const PostActivity = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { successData } = useSelector((state) => state.getAllPostSeasonActivitiesReducer);
    const { successData: districtsData } = useSelector((state) => state.getAllDistrictsReducer);
     const {successData:mandalsData} = useSelector((state) => state.getAllMandalsReducer);
    const { successData: zonesData } = useSelector((state) => state.getAllZonesReducer);
    const { successData: regionsData } = useSelector((state) => state.getAllRegionsReducer);
    const { successData: areasData } = useSelector((state) => state.getAllAreasReducer);
    const { successData: seasonsData } = useSelector((state) => state.getAllSeasonsReducer);

    const District = districtsData?.data?.districts || [];
    const Mandals = mandalsData?.data?.mandals || [];
    const Zones = zonesData?.data?.zones || [];
    const Regions = regionsData?.data?.regions || [];
    const Areas = areasData?.data?.areas || [];
    const Seasons = seasonsData?.data?.seasons || [];

    // View dialog state
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [viewRow, setViewRow] = useState(null);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 30;

    // Consistent desktop grid column widths for perfect alignment
    const gridColsDesktop = '0.9fr 1.6fr 1.2fr 1.2fr 1.2fr 0.9fr 0.8fr 1fr'; // Add one more column for Actions at the end
    const isIpad = useMediaQuery('(min-width:768px) and (max-width:1024px)');


    const stats = [
        { label: "TOTAL TARGETS", value: "0", icon: <TrackChangesIcon />, bg: '#EEF4FF' },
        { label: "ACTIVE TARGETS", value: "0", icon: <CheckCircleIcon />, bg: '#E6FBF4' },
        { label: "INACTIVE TARGETS", value: "0", icon: <CancelIcon />, bg: '#FFF1F1' },
        { label: "COMPLETION RATE", value: "0", icon: <DonutLargeIcon />, bg: '#F4F3FF' },
    ];

    const handleAddTarget = () => {
        navigate('/masterData/createPostActivity');
    };

    // Add view/edit handlers
    const handleView = (activity) => {
        setViewRow(activity);
        setOpenViewDialog(true);
    };
    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setViewRow(null);
    };
    const handleEdit = (activity) => {
        navigate('/masterData/createPostActivity', { state: { mode: 'edit', activity } });
    };

    
    // Filters state
    const [search, setSearch] = useState('');
    const [locationType, setLocationType] = useState('All');
    const [districtName, setDistrictName] = useState('All');
    const [mandalName, setMandalName] = useState('All');
    const [zoneName, setZoneName] = useState('All');
    const [regionName, setRegionName] = useState('All');
    const [areaName, setAreaName] = useState('All');
    const [seasonName, setSeasonName] = useState('All');

    // Map dropdown values to IDs
    const selectedDistrictId = useMemo(() => {
        if (districtName === 'All') return undefined;
        const d = District.find(d => d.name === districtName);
        return d ? d.districtId : undefined;
    }, [districtName, District]);
    const selectedMandalId = useMemo(
        () => {
            if (mandalName === 'All') return undefined;
            const m = Mandals.find(m => m.name === mandalName);
            return m ? m.mandalId : undefined;
        }, [mandalName, Mandals]
    );
    const selectedZoneId = useMemo(() => {
        if (zoneName === 'All') return undefined;
        const z = Zones.find(z => z.zoneName === zoneName);
        return z ? z.zoneId : undefined;
    }, [zoneName, Zones]);
    const selectedRegionId = useMemo(() => {
        if (regionName === 'All') return undefined;
        const r = Regions.find(r => r.regionName === regionName);
        return r ? r.regionId : undefined;
    }, [regionName, Regions]);
    const selectedAreaId = useMemo(() => {
        if (areaName === 'All') return undefined;
        const a = Areas.find(a => a.areaName === areaName);
        return a ? a.areaId : undefined;
    }, [areaName, Areas]);
    const selectedSeasonId = useMemo(() => {
        if (seasonName === 'All') return undefined;
        const s = Seasons.find(s => s.name === seasonName);
        return s ? (s.seasonId || s.season_id || s.id) : undefined;
    }, [seasonName, Seasons]);

    // Helper to get area name by type and id
    const getAreaName = (row) => {
        if (!row || !row.target_area_type || !row.target_area_id) return '';
        const type = row.target_area_type.toLowerCase();
        if (type === 'mandal') {
            const mandal = Mandals.find(m => m.mandalId === row.target_area_id);
            return mandal ? mandal.name : row.target_area_id;
        }
        if (type === 'zone') {
            const zone = Zones.find(z => z.zoneId === row.target_area_id);
            return zone ? zone.zoneName : row.target_area_id;
        }
        if (type === 'region') {
            const region = Regions.find(r => r.regionId === row.target_area_id);
            return region ? region.regionName : row.target_area_id;
        }
        if (type === 'area') {
            const area = Areas.find(a => a.areaId === row.target_area_id);
            return area ? area.areaName : row.target_area_id;
        }
        if (type === 'district') {
            const district = District.find(d => String(d.districtId) === String(row.target_area_id));
            if (district) return district.name;
            // Try to match by name if ID lookup fails
            if (row.target_area && row.target_area.name) return row.target_area.name;
            return row.target_area_id;
        }
        return row.target_area_id;
    };

    // Helper to get season display name
    const getSeasonDisplayName = (row) => {
        if (row.season && (row.season.name || row.season.season_name)) {
            const name = row.season.name || row.season.season_name;
            const year = row.season.year || row.season.season_year || '';
            return year ? `${name} (${year})` : name;
        }
        let seasonId = row.seasonId ?? row.season_id;
        const found = Seasons.find(s => (s.seasonId || s.id) === seasonId || s.id === seasonId);
        if (found) {
            const name = found.name || found.season_name;
            const year = found.year || found.season_year || '';
            return year ? `${name} (${year})` : name;
        }
        return row.seasonId ?? row.season_id;
    };

    // Apply filters
    const filteredData = useMemo(() => {
        const rows = successData?.data || [];
        const norm = (v) => String(v ?? '').toLowerCase();
        return rows.filter((row) => {
            // Normalize IDs for comparison
            const seasonId = row.seasonId ?? row.season_id ?? row?.season?.seasonId ?? row?.season?.season_id;
            const areaType = norm(row.target_area_type);
            // Search filter
            if (search.trim()) {
                const q = norm(search.trim());
                const matches =
                    norm(row?.season?.name).includes(q) ||
                    areaType.includes(q) ||
                    norm(row.dv_count).includes(q) ||
                    norm(row.fd_count).includes(q) ||
                    norm(row.fv_count).includes(q);
                if (!matches) return false;
            }
            // Location type filter
            if (locationType !== 'All') {
                if (locationType === 'District' && areaType !== 'district') return false;
                if (locationType === 'Zone' && areaType !== 'zone') return false;
                if (locationType === 'Region' && areaType !== 'region') return false;
                if (locationType === 'Area' && areaType !== 'area') return false;
            }
            // District filter (apply regardless of locationType)
            if (selectedDistrictId !== undefined) {
                // Show rows where areaType is 'district' and target_area_id matches
                // Or rows where row.districtId matches selectedDistrictId
                // Or rows where areaType is 'mandal' and the mandal's districtId matches
                const mandalMatch = areaType === 'mandal' && (() => {
                    const mandal = Mandals.find(m => m.mandalId === row.target_area_id);
                    return mandal && String(mandal.districtId) === String(selectedDistrictId);
                })();
                if (
                    !(
                        (areaType === 'district' && row.target_area_id === selectedDistrictId) ||
                        (row.districtId && String(row.districtId) === String(selectedDistrictId)) ||
                        mandalMatch
                    )
                ) {
                    return false;
                }
            }
            // Zone filter
            if (locationType === 'Zone' && selectedZoneId !== undefined) {
                if (row.target_area_id !== selectedZoneId) return false;
            }
            // Region filter
            if (locationType === 'Region' && selectedRegionId !== undefined) {
                if (row.target_area_id !== selectedRegionId) return false;
            }
            // Area filter
            if (locationType === 'Area' && selectedAreaId !== undefined) {
                if (row.target_area_id !== selectedAreaId) return false;
            }
            // Season filter
            if (selectedSeasonId !== undefined && String(seasonId) !== String(selectedSeasonId)) return false;
            return true;
        });
    }, [successData?.data, search, locationType, selectedDistrictId, selectedZoneId, selectedRegionId, selectedAreaId, selectedSeasonId]);
   


    // Fetch master data only once on mount
    useEffect(() => {
        dispatch(getAllMandalsAction());
        dispatch(getAllDistrictsAction());
        dispatch(getAllZonesAction());
        dispatch(getAllRegionsAction());
        dispatch(getAllAreasAction());
        dispatch(getAllSeasonsAction());
    }, [dispatch]);

    // Fetch post-season activities when filters change
    useEffect(() => {
        const queryParams = {};
        if (selectedZoneId) queryParams.zoneId = selectedZoneId;
        if (selectedRegionId) queryParams.regionId = selectedRegionId;
        if (selectedAreaId) queryParams.areaId = selectedAreaId;
        if (selectedDistrictId) queryParams.districtId = selectedDistrictId;
        if (selectedSeasonId) queryParams.seasonId = selectedSeasonId;
        // For debugging
        console.log('Query Params sent to backend:', queryParams);
        const paramsArr = [];
        if (selectedSeasonId) paramsArr.push(`seasonId=${selectedSeasonId}`);
        if (selectedZoneId) paramsArr.push(`zoneId=${selectedZoneId}`);
        if (selectedRegionId) paramsArr.push(`regionId=${selectedRegionId}`);
        if (selectedAreaId) paramsArr.push(`areaId=${selectedAreaId}`);
        if (selectedDistrictId) paramsArr.push(`districtId=${selectedDistrictId}`);
        const query = paramsArr.length ? `target/post-season?${paramsArr.join('&')}` : 'target/post-season';
        dispatch(getAllPostSeasonActivitiesAction({ params: queryParams, endPoint: query }));
    }, [dispatch, selectedZoneId, selectedRegionId, selectedAreaId, selectedDistrictId, selectedSeasonId]);

    // Reset page when filters change
    useEffect(() => { setPage(1); }, [search, locationType, districtName, zoneName, regionName, areaName, seasonName]);

    const totalPages = Math.max(1, Math.ceil((filteredData?.length ?? 0) / PAGE_SIZE));
    const pagedData = (filteredData ?? []).slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* PAGE HEADER */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt:{sm:7,md:0} }}>
                Post Activity
            </Typography>

            {/* STATS CARDS */}
            {/* <Box sx={{ width: '100%', mb: 3 }}>
                <Grid
                    container
                    spacing={{ xs: 1, sm: 1, md: 1 }}
                    sx={{
                        width: '100%',
                        m: 0,

                    }}
                >
                    {stats.map((s) => (
                        <Grid
                            item
                            size={{ xs: 6, sm: 3, md: 3 }}
                            key={s.label}
                            sx={{
                                p: 0,
                                display: 'flex',
                                justifyContent: 'stretch',
                            }}
                        >
                            <Card sx={{ p: isIpad ? 1.5 : 2, borderRadius: 3, height: isIpad ? 60 : 50, width: '100%', mx: 0, display: 'flex', alignItems: 'center' }}>
                                <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: { xs: '0.85rem', sm: 'inherit' },
                                                textAlign: 'left',
                                                mb: 0,
                                            }}
                                        >
                                            {s.label}
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            fontWeight={600}
                                            sx={{
                                                fontSize: { xs: '1.1rem', sm: isIpad ? '1.4rem' : '1.5rem' },
                                                textAlign: 'left',
                                                mb: 0,
                                            }}
                                        >
                                            {s.value}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            bgcolor: s.bg,
                                            width: isIpad ? 44 : 45,
                                            height: isIpad ? 44 : 45,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 2,
                                        }}
                                    >
                                        {s.icon}
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box> */}


            {/* FILTER SECTION */}
            <Card sx={{ mr: { xs: 1 }, p: 2, borderRadius: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search ..."
                            slotProps={{
                                input: {
                                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                                },
                            }}
                            fullWidth
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                },
                            }}
                        />
                    </Grid>

                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select
                            size="small"
                            value={locationType}
                            onChange={(e) => {
                                const val = e.target.value;
                                setLocationType(val);
                                if (val !== 'District') setDistrictName('All');
                                if (val !== 'Zone') setZoneName('All');
                                if (val !== 'Region') setRegionName('All');
                                if (val !== 'Area') setAreaName('All');
                            }}
                            sx={{ width: '100%', borderRadius: 3 }}
                        >
                            <MenuItem value="All">All Locations</MenuItem>
                            <MenuItem value="District">District</MenuItem>
                            <MenuItem value="Zone">Zone</MenuItem>
                            <MenuItem value="Region">Region</MenuItem>
                            <MenuItem value="Area">Area</MenuItem>
                        </Select>
                    </Grid>

                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select
                            size="small"
                            value={districtName}
                            onChange={(e) => setDistrictName(e.target.value)}
                            sx={{ width: '100%', borderRadius: 3 }}
                        >
                            <MenuItem value="All">All Districts</MenuItem>
                            {District.map((d) => (
                                <MenuItem key={d.districtId} value={d.name}>{d.name}</MenuItem>
                            ))}
                        </Select>
                    </Grid>

                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select
                            size="small"
                            value={zoneName}
                            onChange={(e) => setZoneName(e.target.value)}
                            sx={{ width: '100%', borderRadius: 3 }}
                        >
                            <MenuItem value="All">All Zones</MenuItem>
                            {Zones.map((z) => (
                                <MenuItem key={z.zoneId} value={z.zoneName}>{z.zoneName}</MenuItem>
                            ))}
                        </Select>
                    </Grid>

                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select
                            size="small"
                            value={regionName}
                            onChange={(e) => setRegionName(e.target.value)}
                            sx={{ width: '100%', borderRadius: 3 }}
                        >
                            <MenuItem value="All">All Regions</MenuItem>
                            {Regions.map((r) => (
                                <MenuItem key={r.regionId} value={r.regionName}>{r.regionName}</MenuItem>
                            ))}
                        </Select>
                    </Grid>

                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select
                            size="small"
                            value={areaName}
                            onChange={(e) => setAreaName(e.target.value)}
                            sx={{ width: '100%', borderRadius: 3 }}
                        >
                            <MenuItem value="All">All Areas</MenuItem>
                            {Areas.map((a) => (
                                <MenuItem key={a.areaId} value={a.areaName}>{a.areaName}</MenuItem>
                            ))}
                        </Select>
                    </Grid>

                    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
                        <Select
                            size="small"
                            value={seasonName}
                            onChange={(e) => setSeasonName(e.target.value)}
                            sx={{ width: '100%', borderRadius: 3 }}
                        >
                            <MenuItem value="All">All Seasons</MenuItem>
                            {Seasons.map((s) => (
                                <MenuItem key={s.seasonId} value={s.name}>{s.name}</MenuItem>
                            ))}
                        </Select>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
                        <Button
                            variant="contained"
                            sx={{
                                borderRadius: 2,
                                whiteSpace: 'nowrap',
                                '@media (min-width:1100px) and (max-width:1500px)': {
                                    fontSize: '0.70rem',
                                },
                            }}
                            fullWidth
                            onClick={handleAddTarget}
                        >
                            + Add New Target
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            {/* CONTENT AREA */}
            <Box sx={{ mt: 3 }}>
                {isMobile ? (
                    <Grid container spacing={2}>
                        {pagedData?.map((row) => (
                            <Grid item size={{ xs: 12 }} key={row.id ?? row.seasonId}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: 3,
                                        p: 1.5,
                                        border: '1px solid #E5E7EB',
                                        '&:hover': {
                                            border: '2px solid #2563EB',
                                            boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                        },
                                    }}
                                >
                                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                                        <Typography fontWeight={600} minWidth={88}>Season:</Typography>
                                        <Typography variant='body2'>{getSeasonDisplayName(row)}</Typography>
                                        <Box ml="auto">
                                            <Chip
                                                label={row.is_active ? 'Active' : 'Inactive'}
                                                size="small"
                                                sx={{
                                                    backgroundColor: row.is_active ? '#D1FAE5' : '#FEE2E2',
                                                    height: 24,
                                                    '& .MuiChip-label': { fontSize: 11, fontWeight: 600 },
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                                        <Typography fontWeight={600} minWidth={88}>Area Type:</Typography>
                                        <Typography variant='body2'>{row?.target_area_type}</Typography>
                                    </Box>
                                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                                        <Typography fontWeight={600} minWidth={88}>Area Name:</Typography>
                                        <Typography variant='body2'>{getAreaName(row)}</Typography>
                                    </Box>

                                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                                        <Typography fontWeight={600} minWidth={88}>DV Count:</Typography>
                                        <Typography variant='body2'>{row?.dv_done_count ?? 0}/{row?.dv_count ?? 0}</Typography>
                                    </Box>

                                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                                        <Typography fontWeight={600} minWidth={88}>FD Count:</Typography>
                                        <Typography variant='body2'>{row?.fd_done_count ?? 0}/{row?.fd_count ?? 0}</Typography>
                                    </Box>

                                    <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                                        <Typography fontWeight={600} minWidth={88}>FV Count:</Typography>
                                        <Typography variant='body2'>{row?.fv_done_count ?? 0}/{row?.fv_count ?? 0}</Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} mt={1}>
                                        <IconButton size="small" aria-label="view" onClick={() => handleView(row)}>
                                            <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleEdit(row)}>
                                            <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                        </IconButton>
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                        {(filteredData?.length ?? 0) === 0 && (
                            <Grid item size={{ xs: 12 }}>
                                <Typography variant="body2" color="text.secondary">No post activities found.</Typography>
                            </Grid>
                        )}
                        {totalPages > 1 && (
                            <Grid item size={{ xs: 12 }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} flexWrap="wrap" gap={1}>
                                    <Typography variant="body2" >
                                        Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredData.length)} to {Math.min(page * PAGE_SIZE, filteredData.length)} of {filteredData.length} entries
                                    </Typography>
                                    <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" size="small" />
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                ) : (
                    <Box display="flex" flexDirection="column" gap={1}>
                        {/* Desktop header row */}
                        <Box
                            elevation={0}
                            sx={{
                                display: { xs: 'none', sm: 'grid' },
                                gridTemplateColumns: { sm: gridColsDesktop },
                                alignItems: 'center',
                                px: 2,
                                py: 2,
                                columnGap: 0,
                            }}
                        >
                            {['Season ID', 'Area Type', 'Area Name', 'DV Count', 'FD Count', 'FV Count', 'Status', 'Actions'].map((h) => (
                                <Typography
                                    key={h}
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ textTransform: 'uppercase', textAlign: h === 'Actions' ? 'right' : 'left' }}
                                >
                                    {h}
                                </Typography>
                            ))}
                        </Box>

                        {/* Desktop value rows */}
                        {pagedData?.map((row) => (
                            <Paper
                                key={row.id ?? row.seasonId}
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    display: { xs: 'none', sm: 'grid' },
                                    gridTemplateColumns: { sm: gridColsDesktop },
                                    alignItems: 'center',
                                    border: '1px solid #E5E7EB',
                                    px: 1.2,
                                    py: 1,
                                    gap: 0.5,
                                    columnGap: 0.5,
                                    '&:hover': {
                                        border: '2px solid #2563EB',
                                        boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                    },
                                }}
                            >
                                <Typography variant="body2">{getSeasonDisplayName(row)}</Typography>
                                <Typography variant="body2">{row.target_area_type}</Typography>
                                <Typography variant="body2">{getAreaName(row)}</Typography>
                                <Typography variant="body2">{row.dv_done_count ?? 0}/{row.dv_count ?? 0}</Typography>
                                <Typography variant="body2">{row.fd_done_count ?? 0}/{row.fd_count ?? 0}</Typography>
                                <Typography variant="body2">{row.fv_done_count ?? 0}/{row.fv_count ?? 0}</Typography>
                                <Chip
                                    label={row.is_active ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{
                                        width: 90,
                                        backgroundColor: row.is_active ? '#D1FAE5' : '#FEE2E2',
                                        ml: -2,
                                        height: 24,
                                        '& .MuiChip-label': { fontSize: 12, fontWeight: 600 },
                                    }}
                                />
                                <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                                    <IconButton size="small" aria-label="view" onClick={() => handleView(row)}>
                                        <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                    </IconButton>
                                    <IconButton size="small" aria-label="edit" onClick={() => handleEdit(row)}>
                                        <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                    </IconButton>
                                </Box>
                            </Paper>
                        ))}
                        {(filteredData?.length ?? 0) === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>No post activities found.</Typography>
                        )}
                        {totalPages > 1 && (
                            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
                                <Typography variant="body2" >
                                    Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredData.length)} to {Math.min(page * PAGE_SIZE, filteredData.length)} of {filteredData.length} entries
                                </Typography>
                                <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            <PostActivityDetailsView
                open={openViewDialog}
                onClose={handleCloseViewDialog}
                row={viewRow}
            />
        </Box>
    );
};

export default PostActivity;